import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './store/authStore';
import { AppShell } from './components/AppShell';
import { LoadingOrb } from './components/LoadingOrb';
import { PageTransition } from './components/PageTransition';
import { SetupProvider } from './store/setupStore';
import { AlertToastProvider } from './store/alertToastStore';
import { SuccessToastProvider } from './store/successToastStore';
// Lazy loaded components
const SplashScreen = lazy(() => import('./pages/SplashScreen').then(m => ({
  default: m.SplashScreen
})));
const IntroSlides = lazy(() => import('./pages/IntroSlides').then(m => ({
  default: m.IntroSlides
})));
const RegisterScreen = lazy(() => import('./pages/RegisterScreen').then(m => ({
  default: m.RegisterScreen
})));
const LoginScreen = lazy(() => import('./pages/LoginScreen').then(m => ({
  default: m.LoginScreen
})));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({
  default: m.ForgotPassword
})));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({
  default: m.ResetPassword
})));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({
  default: m.Dashboard
})));
const MonitorScreen = lazy(() => import('./pages/MonitorScreen').then(m => ({
  default: m.MonitorScreen
})));
const SessionsList = lazy(() => import('./pages/history/SessionsList').then(m => ({
  default: m.SessionsList
})));
const SessionDetail = lazy(() => import('./pages/history/SessionDetail').then(m => ({
  default: m.SessionDetail
})));
const ShareReport = lazy(() => import('./pages/history/ShareReport').then(m => ({
  default: m.ShareReport
})));
const AppointmentsList = lazy(() => import('./pages/appointments/AppointmentsList').then(m => ({
  default: m.AppointmentsList
})));
const LearnHome = lazy(() => import('./pages/learn/LearnHome').then(m => ({
  default: m.LearnHome
})));
const LearnSection = lazy(() => import('./pages/learn/LearnSection').then(m => ({
  default: m.LearnSection
})));
const MotherScreen = lazy(() => import('./pages/mother/MotherScreen').then(m => ({
  default: m.MotherScreen
})));
const BabyScreen = lazy(() => import('./pages/baby/BabyScreen').then(m => ({
  default: m.BabyScreen
})));
const AuroraChat = lazy(() => import('./pages/aurora/AuroraChat').then(m => ({
  default: m.AuroraChat
})));
const AuroraHistory = lazy(() => import('./pages/aurora/AuroraHistory').then(m => ({
  default: m.AuroraHistory
})));
const AuroraConversationView = lazy(() => import('./pages/aurora/AuroraConversationView').then(m => ({
  default: m.AuroraConversationView
})));
const AlertsList = lazy(() => import('./pages/alerts/AlertsList').then(m => ({
  default: m.AlertsList
})));
const AlertDetail = lazy(() => import('./pages/alerts/AlertDetail').then(m => ({
  default: m.AlertDetail
})));
const ProfileScreen = lazy(() => import('./pages/profile/ProfileScreen').then(m => ({
  default: m.ProfileScreen
})));
const EditProfile = lazy(() => import('./pages/profile/EditProfile').then(m => ({
  default: m.EditProfile
})));
const DeviceManagement = lazy(() => import('./pages/profile/DeviceManagement').then(m => ({
  default: m.DeviceManagement
})));
const DeviceEntry = lazy(() => import('./pages/setup/DeviceEntry').then(m => ({
  default: m.DeviceEntry
})));
const WaitingActivation = lazy(() => import('./pages/setup/WaitingActivation').then(m => ({
  default: m.WaitingActivation
})));
const SetupSuccess = lazy(() => import('./pages/setup/SetupSuccess').then(m => ({
  default: m.SetupSuccess
})));
// Route Guard for unauthenticated users trying to access protected routes
function ProtectedRoute({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isAuthenticated,
    isLoading
  } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return <FullScreenLoader data-id="element-0" data-mp="h72ow2n45ie9" />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/splash" state={{
      from: location
    }} replace data-id="element-1" data-mp="h59imogex06s" />;
  }
  return <>{children}</>;
}
// Route Guard for authenticated users trying to access auth screens
function AuthRoute({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isAuthenticated,
    isLoading
  } = useAuth();
  if (isLoading) {
    return <FullScreenLoader data-id="element-2" data-mp="zwsg6fro3m1f" />;
  }
  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace data-id="element-3" data-mp="vw8k7fityogv" />;
  }
  return <>{children}</>;
}
function FullScreenLoader() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const startTime = Date.now();
    const duration = 1500;
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      setProgress(eased * 100);
      if (t < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, []);
  return <div className="min-h-screen w-full bg-secondary flex flex-col items-center justify-center" data-id="element-4" data-mp="2530ge346pygg">
      <LoadingOrb data-id="element-5" data-mp="29xwlo4yfmibi" />
      <h1 className="font-sans text-[28px] font-semibold text-text-primary tracking-[4px] uppercase mt-6 mb-6" data-id="element-6" data-mp="2euwtakaxjt4q">
        Aurora Care
      </h1>
      <div className="w-[120px] h-[3px] bg-border rounded-pill overflow-hidden" data-id="element-7" data-mp="2jsyd57g6jwh9">
        <div className="h-full bg-primary rounded-pill transition-none" style={{
        width: `${progress}%`
      }} data-id="element-8" data-mp="2nv2zdl4atqtv" />
      </div>
    </div>;
}
function AppRoutes() {
  return <Suspense fallback={<FullScreenLoader data-id="element-10" data-mp="tj4vlyordn5d" />} data-id="element-9" data-mp="timwmuapoutr">
      <Routes data-id="element-11" data-mp="tjxzmrwc1yjt">
        {/* Public / Auth Routes */}
        <Route path="/" element={<Navigate to="/splash" replace data-id="element-13" data-mp="ygwuxdrqgbjl" />} data-id="element-12" data-mp="yhg15x80tbhs" />
        <Route path="/splash" element={<AuthRoute data-id="element-15" data-mp="21tojq66hn39e">
              <PageTransition type="fade" data-id="element-16" data-mp="1wsqzzp5ty15s">
                <SplashScreen data-id="element-17" data-mp="1wtsmcpsaqa2t" />
              </PageTransition>
            </AuthRoute>} data-id="element-14" data-mp="21r29tzw4kbo0" />
        <Route path="/intro" element={<AuthRoute data-id="element-19" data-mp="25snuw8itbw9a">
              <PageTransition type="fade" data-id="element-20" data-mp="25se4wkxrpp3l">
                <IntroSlides data-id="element-21" data-mp="25viexyxwzf26" />
              </PageTransition>
            </AuthRoute>} data-id="element-18" data-mp="2attqce3uxxmi" />
        <Route path="/register" element={<AuthRoute data-id="element-23" data-mp="2fqtugjh5cqaz">
              <PageTransition type="slideUp" data-id="element-24" data-mp="2fpirumzftolg">
                <RegisterScreen data-id="element-25" data-mp="2thowbbt8a752" />
              </PageTransition>
            </AuthRoute>} data-id="element-22" data-mp="2frd3faotk4cv" />
        <Route path="/login" element={<AuthRoute data-id="element-27" data-mp="33go8h4d7ckwt">
              <PageTransition type="slideUp" data-id="element-28" data-mp="33g4rpqzag2kd">
                <LoginScreen data-id="element-29" data-mp="33etmrnu3woro" />
              </PageTransition>
            </AuthRoute>} data-id="element-26" data-mp="2pi7t6p19xm32" />
        <Route path="/forgot-password" element={<AuthRoute data-id="element-31" data-mp="3i464lecp877m">
              <PageTransition type="slideUp" data-id="element-32" data-mp="3i1k05nl3u7tm">
                <ForgotPassword data-id="element-33" data-mp="3n2zjnb7mw69w" />
              </PageTransition>
            </AuthRoute>} data-id="element-30" data-mp="2yfqii5lyamkr" />
        <Route path="/reset-password" element={<PageTransition type="slideUp" data-id="element-35" data-mp="3d4a0zg7wyhsl">
              <ResetPassword data-id="element-36" data-mp="3d2yv9auwqzyu" />
            </PageTransition>} data-id="element-34" data-mp="384f9bagbdo6h" />

        {/* Protected Routes */}
        <Route path="/app/*" element={<ProtectedRoute data-id="element-38" data-mp="3443nhuc7thtf">
              <DashboardRoutes data-id="element-39" data-mp="345evy7glvdqv" />
            </ProtectedRoute>} data-id="element-37" data-mp="2zbu5d0hcdm1p" />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/splash" replace data-id="element-41" data-mp="2ue2074ros3fk" />} data-id="element-40" data-mp="2pgse9z3ia9wh" />
      </Routes>
    </Suspense>;
}
function DashboardRoutes() {
  return <Suspense fallback={<FullScreenLoader data-id="element-43" data-mp="2gfc3vrcoy7s9" />} data-id="element-42" data-mp="2gdr8y0mgsmtv">
      <Routes data-id="element-44" data-mp="2gf2oexp9vj0t">
        {/* Setup flow doesn't use the AppShell */}
        <Route path="setup/*" element={<SetupProvider data-id="element-46" data-mp="30vdiw67p8t30">
              <Routes data-id="element-47" data-mp="30vnb80g7vcbv">
                <Route path="" element={<PageTransition type="slideUp" data-id="element-49" data-mp="2vw0uvi53dfyu">
                      <DeviceEntry data-id="element-50" data-mp="2vug3u2hzkf5q" />
                    </PageTransition>} data-id="element-48" data-mp="30u2dk1ylvd9s" />
                <Route path="waiting" element={<PageTransition type="slideRight" data-id="element-52" data-mp="2m0h4fb9ar9hh">
                      <WaitingActivation data-id="element-53" data-mp="2hxkq3wqshzjc" />
                    </PageTransition>} data-id="element-51" data-mp="2ly4pz82qzh96" />
                <Route path="success" element={<PageTransition type="slideUp" data-id="element-55" data-mp="287hq3cl93lbi">
                      <SetupSuccess data-id="element-56" data-mp="237n0rdh4j3sk" />
                    </PageTransition>} data-id="element-54" data-mp="2d485ryt58ewe" />
              </Routes>
            </SetupProvider>} data-id="element-45" data-mp="2kg4h0vooowtt" />

        {/* Monitor screen is full screen, outside AppShell */}
        <Route path="monitor" element={<PageTransition type="slideUp" data-id="element-58" data-mp="9p66u6rberve">
              <MonitorScreen data-id="element-59" data-mp="9o4jb2t44cws" />
            </PageTransition>} data-id="element-57" data-mp="1u7zxem4u3nwv" />

        {/* All other app routes use the AppShell */}
        <Route path="*" element={<AppShell data-id="element-61" data-mp="iqm3lbp7ivh5">
              <Routes data-id="element-62" data-mp="iqdr3kwzxs6h">
                <Route path="dashboard" element={<PageTransition type="fade" data-id="element-64" data-mp="nmkxfetb15oy">
                      <Dashboard data-id="element-65" data-mp="3m7cgmaj7i8sc" />
                    </PageTransition>} data-id="element-63" data-mp="inridhydd6mo" />
                <Route path="mother" element={<PageTransition type="fade" data-id="element-67" data-mp="3w47celo3hv2x">
                      <MotherScreen data-id="element-68" data-mp="3w2e7bat4rix1" />
                    </PageTransition>} data-id="element-66" data-mp="3r6fm1w33mh0u" />
                <Route path="baby" element={<PageTransition type="fade" data-id="element-70" data-mp="33dpd7vqhsdus">
                      <BabyScreen data-id="element-71" data-mp="38e4p0iv4ldan" />
                    </PageTransition>} data-id="element-69" data-mp="4rmjmhcn34rb" />
                <Route path="history" element={<PageTransition type="fade" data-id="element-73" data-mp="2k5ot8tyed53c">
                      <SessionsList data-id="element-74" data-mp="2k4dj8cecz13s" />
                    </PageTransition>} data-id="element-72" data-mp="2f7wvhjb2mor8" />
                <Route path="history/:sessionId" element={<PageTransition type="slideRight" data-id="element-76" data-mp="2u2bcxfezlq3s">
                      <SessionDetail data-id="element-77" data-mp="1whosr2baa0bk" />
                    </PageTransition>} data-id="element-75" data-mp="2p38c6761i61m" />
                <Route path="history/:sessionId/share" element={<PageTransition type="slideRight" data-id="element-79" data-mp="26d8o9nlxqwzs">
                      <ShareReport data-id="element-80" data-mp="2b59yz53ggfnp" />
                    </PageTransition>} data-id="element-78" data-mp="21gibb95fghil" />
                <Route path="appointments" element={<PageTransition type="fade" data-id="element-82" data-mp="1ioz4ssc72lbt">
                      <AppointmentsList data-id="element-83" data-mp="1inecl9dcreh4" />
                    </PageTransition>} data-id="element-81" data-mp="1dqnygrl3ysyc" />
                <Route path="learn" element={<PageTransition type="fade" data-id="element-85" data-mp="3ryotp4udoetx">
                      <LearnHome data-id="element-86" data-mp="3myavsfmhql9m" />
                    </PageTransition>} data-id="element-84" data-mp="3vxeehihqwynl" />
                <Route path="learn/:slug" element={<PageTransition type="slideRight" data-id="element-88" data-mp="3d167a956adpn">
                      <LearnSection data-id="element-89" data-mp="3d1xsqr522v27" />
                    </PageTransition>} data-id="element-87" data-mp="3i094j7ewvfn2" />
                <Route path="aurora" element={<PageTransition type="fade" data-id="element-91" data-mp="348kowu5ge91c">
                      <AuroraChat data-id="element-92" data-mp="2z9rmpxvma4gh" />
                    </PageTransition>} data-id="element-90" data-mp="387tkm4k20gzj" />
                <Route path="aurora/history" element={<PageTransition type="slideRight" data-id="element-94" data-mp="2pak1ypmjz7jc">
                      <AuroraHistory data-id="element-95" data-mp="2pd531cf11jg1" />
                    </PageTransition>} data-id="element-93" data-mp="2uaepcj74dezn" />
                <Route path="aurora/history/:conversationId" element={<PageTransition type="slideRight" data-id="element-97" data-mp="26mrok6ttd8ed">
                      <AuroraConversationView data-id="element-98" data-mp="26ocibu8b0nb6" />
                    </PageTransition>} data-id="element-96" data-mp="2fpsrjyndujdt" />
                <Route path="alerts" element={<PageTransition type="fade" data-id="element-100" data-mp="2yepn05uinhxm">
                      <AlertsList data-id="element-101" data-mp="33dk4n4gqz0g5" />
                    </PageTransition>} data-id="element-99" data-mp="2aqfvqoeja1z3" />
                <Route path="alerts/:alertId" element={<PageTransition type="slideRight" data-id="element-103" data-mp="2tmpehp0drgpf">
                      <AlertDetail data-id="element-104" data-mp="2tl4ma61jg9uq" />
                    </PageTransition>} data-id="element-102" data-mp="2pmfc0mbsr8fb" />
                <Route path="profile" element={<PageTransition type="fade" data-id="element-106" data-mp="3m5mospslkyv5">
                      <ProfileScreen data-id="element-107" data-mp="1anrk39y6kt27" />
                    </PageTransition>} data-id="element-105" data-mp="3hayycle19udx" />
                <Route path="profile/edit" element={<PageTransition type="slideRight" data-id="element-109" data-mp="1kem8okywg4qt">
                      <EditProfile data-id="element-110" data-mp="1kg72g8de3jnm" />
                    </PageTransition>} data-id="element-108" data-mp="15oeystmyju1q" />
                <Route path="profile/devices" element={<PageTransition type="slideRight" data-id="element-112" data-mp="rri7kr2xgakb">
                      <DeviceManagement data-id="element-113" data-mp="mrvuqir0gwc1" />
                    </PageTransition>} data-id="element-111" data-mp="rpp0jaoejobs" />
                <Route path="*" element={<Navigate to="dashboard" replace data-id="element-115" data-mp="11lib4oik97qx" />} data-id="element-114" data-mp="11pfth7xbdv9r" />
              </Routes>
            </AppShell>} data-id="element-60" data-mp="dscar7qyg6yp" />
      </Routes>
    </Suspense>;
}
export function App() {
  return <AuthProvider data-id="element-116" data-mp="2d54jk85v7aes">
      <AlertToastProvider data-id="element-117" data-mp="283xckbbjqf96">
        <SuccessToastProvider data-id="element-118" data-mp="284q9kkkpdqe4">
          <BrowserRouter data-id="element-119" data-mp="283f50ijfoely">
            <AppRoutes data-id="element-120" data-mp="2869q8ca9u3gf" />
          </BrowserRouter>
        </SuccessToastProvider>
      </AlertToastProvider>
    </AuthProvider>;
}